<template>
  <div class="article-head-details">
    <div
      v-if="theme === 'default'"
      class="article-head-details-share selection:md:mb-4 flex flex-wrap md:block items-center"
    >
      <div
        v-if="hasShareButtons"
        class="inline-block md:block mb-4 md:mb-5 mr-4 md:mr-0"
      >
        <MoleculesShareButtons
          class=""
          :title="devices.isMobile.value ? '': 'Share this article'"
          :buttons="[
            ...shareButtons,
            {
              name: 'Copy',
              hideTextOnMobile: true,
              url: articleLink
            }
          ]"
        />
      </div>

      <div class="flex flex-wrap gap-4 mb-4 uppercase">
        <AtomsFactChecked
          v-if="_factChecked"
          class="inline-flex"
          :collapse="factCheckedCollapse"
        />

        <atoms-tag
          v-for="(tag, index) in _tags"
          :key="index"
          class="text-sm tag"
        >
          {{ tag.name }}
        </atoms-tag>
      </div>
    </div>

    <div class="article-head-details-author flex items-center flex-wrap lg:pb-0 gap-3 md:gap-4">
      <atoms-media-objects
        :class="{
          'gap-2': theme === 'v2',
          'gap-[0.875rem]': theme === 'default'
        }"
        label-class="!ml-0"
      >
        <template #image>
          <component
            :is="AUTHOR_URL ? 'a' : 'div'"
            :href="AUTHOR_URL"
          >
            <NuxtImg
              :src="`${author?.image?.url}?w=96&h=96`"
              :alt="author?.name ?? 'author'"
              width="40"
              height="40"
              class="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover"
            />
          </component>
        </template>

        <component
          :is="AUTHOR_URL ? 'a' : 'p'"
          class="font-bold text-base"
          :href="AUTHOR_URL"
          :class="{
            'text-semantic-primary-default': theme === 'default'
          }"
        >
          {{ author?.name }}
        </component>
      </atoms-media-objects>

      <div :class="divider"></div>

      <div class="text-sm">
        Updated {{ stringToDate(date) }}
      </div>

      <div
        v-if="theme === 'default'"
        class="hidden md:block"
        :class="divider"
      >
      </div>

      <div
        v-if="theme === 'v2'"
        class="hidden md:inline-flex items-center flex-wrap uppercase gap-4"
      >
        <atoms-tag
          v-for="(tag, index) in _tags"
          :key="index"
          color="pink"
          class="text-sm tag"
        >
          {{ tag.name }}
        </atoms-tag>
      </div>

      <template v-if="theme === 'v2'">
        <MoleculesShareButtons
          title="Share"
          theme="dropdown"
          :buttons="shareButtons"
        />

        <AtomsFactChecked
          v-if="_factChecked"
          :collapse="factCheckedCollapse"
        />
      </template>

      <atoms-button-copy
        v-else
        :text="articleLink"
        size="small"
        class="hidden md:inline-flex"
      >
        Click Link
      </atoms-button-copy>
    </div>
  </div>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'
import dayjs from 'dayjs'

defineOptions({
  name: 'MoleculesHeadDetails'
})

const { devices } = deviceChecker()
const {
  internalDomainForInlinesHyperlink
} = useEnv()

const props = defineProps({
  theme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'v2'].includes(value)
  },

  title: {
    type: String,
    default: ''
  },

  author: {
    type: Object,
    default: () => ({})
  },

  date: {
    type: String,
    default: ''
  },

  tags: {
    type: Array,
    default: () => []
  },

  hasShareButtons: {
    type: Boolean,
    default: false
  },

  factCheckedCollapse: {
    type: Boolean,
    default: false
  }
})

const divider = getKey(props.theme, {
  // line
  default: 'w-px h-[20px] border-r border-r-gray-400',

  // circle
  v2: 'inline-block w-2 h-2 rounded-full bg-semantic-primary-default'
})

const _factChecked = computed(() => !!(props.tags.find(item => item.name.toLowerCase() === 'fact checked')))

const _tags = computed(() => {
  const tags = props.tags.filter(item => item.name.toLowerCase() !== 'fact checked')

  return !_isEmpty(tags) ? tags : []
})

const articleLink = import.meta.client ? window.location.href : ''

const shareButtons = [
  {
    name: 'Facebook',
    url: `https://www.facebook.com/sharer/sharer.php?u=${articleLink}`
  },
  {
    name: 'Twitter',
    url: `https://twitter.com/share?text=${props.title}&url=${articleLink}`
  },
  {
    name: 'Linkedin',
    url: `https://www.linkedin.com/shareArticle?mini=true&url=${articleLink}`
  },
  {
    name: 'Whatsapp',
    url: `https://api.whatsapp.com/send?text=${articleLink}`
  }
]

const stringToDate = date => dayjs(date).format('DD/MM/YYYY')

const AUTHOR_URL = computed(() => {
  const origin = (import.meta.client && window.location.origin) || internalDomainForInlinesHyperlink

  const url = props.author?.url

  if (!url) {
    return url
  }

  if (!(/(http(s?)):\/\//i.test(url))) {
    return trailingSlash(`${origin}${url}`)
  }
  return trailingSlash(url)
})
</script>
